

import {
    TextInput, Textarea, NumberInput, Button, SegmentedControl,
    Radio, Checkbox, Select, Rating, Group, Text, Box, Stack
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
    PageElement, ShortTextElement, MultilineElement, DateElement,
    NumberElement, YesNoElement, MultiChoiceElement, RatingElement,
    ButtonElement, RichTextElement, ImageUploadElement
} from '@holloway/dsl';
import { IconChevronRight, IconPhoto } from '@tabler/icons-react';
import { isEmptyRichText, renderRichTextWithVars, renderTextWithVars, VAR_BUBBLE_CSS } from '@/features/editor/utils/renderRichText';

// Helper to render the specific element content
export function renderElementContent(
    element: PageElement,
    onChange?: (updates: Partial<PageElement>) => void,
    variables: { id: string; label: string }[] = [],
    options: { fullPreview?: boolean } = {}
) {
    const previewVars = Object.fromEntries(variables.map((variable) => [variable.id, '']));
    // Mention labels are baked into stored content at insert time, so a rename
    // only reaches existing rich text through this map.
    const variableLabels = Object.fromEntries(variables.map((variable) => [variable.id, variable.label]));
    const renderLabel = (label?: string) => renderTextWithVars(label || '', previewVars);

    switch (element.type) {
        case 'ShortText':
            const shortText = element as ShortTextElement;
            return (
                <TextInput
                    placeholder={shortText.placeholder}
                    label={renderLabel(shortText.label)}
                    description="Short text input"
                    readOnly
                    style={{ pointerEvents: 'none' }}
                />
            );

        case 'Multiline':
            const multiline = element as MultilineElement;
            return (
                <Textarea
                    placeholder={multiline.placeholder}
                    label={renderLabel(multiline.label)}
                    minRows={multiline.rows}
                    description="Multiline text input"
                    readOnly
                    style={{ pointerEvents: 'none' }}
                />
            );

        case 'Date':
            const dateEl = element as DateElement;
            return (
                <DateInput
                    placeholder="Pick a date"
                    label={renderLabel(dateEl.label)}
                    description={`Date format: ${dateEl.dateFormat}`}
                    readOnly
                    style={{ pointerEvents: 'none' }}
                />
            );

        case 'Number':
            const numberEl = element as NumberElement;
            return (
                <NumberInput
                    placeholder="Enter number"
                    label={renderLabel(numberEl.label)}
                    min={numberEl.min}
                    max={numberEl.max}
                    readOnly
                    style={{ pointerEvents: 'none' }}
                />
            );

        case 'YesNo':
            const yesNo = element as YesNoElement;
            return (
                <Box>
                    <Text size="sm" fw={500} mb={4}>{renderLabel(yesNo.label)}</Text>
                    <SegmentedControl
                        data={[yesNo.yesLabel || 'Yes', yesNo.noLabel || 'No']}
                        fullWidth
                        readOnly
                        style={{ pointerEvents: 'none' }}
                    />
                </Box>
            );

        case 'MultiChoice':
            const multiChoice = element as MultiChoiceElement;
            const isBranchingMultiChoice = (multiChoice as any).createBranches && !(multiChoice as any).allowMultiple;
            return (
                <Box>
                    <Text size="sm" fw={500} mb={4}>{renderLabel(multiChoice.label)}</Text>
                    {isBranchingMultiChoice ? (
                        <Stack
                            gap={0}
                            style={{
                                borderTop: '1px solid #D9DEE8',
                                backgroundColor: '#FFFFFF',
                            }}
                        >
                            {multiChoice.options.map((opt, i) => (
                                <Box
                                    key={i}
                                    style={{
                                        pointerEvents: 'none',
                                        borderBottom: '1px solid #D9DEE8',
                                        minHeight: 46,
                                        padding: '10px 8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                    }}
                                >
                                    <Text size="sm" fw={500} c="#2A3140" style={{ lineHeight: 1.35 }}>
                                        {opt}
                                    </Text>
                                    <IconChevronRight size={18} color="#415A91" style={{ flexShrink: 0 }} />
                                </Box>
                            ))}
                        </Stack>
                    ) : multiChoice.displayStyle === 'dropdown' ? (
                        <Select
                            placeholder="Select an option"
                            data={multiChoice.options}
                            readOnly
                            style={{ pointerEvents: 'none' }}
                        />
                    ) : (multiChoice as any).displayStyle === 'buttons' ? (
                        <Stack gap="xs">
                            {multiChoice.options.map((opt, i) => (
                                <Button key={i} variant="outline" fullWidth justify="flex-start" style={{ pointerEvents: 'none' }}>
                                    {opt}
                                </Button>
                            ))}
                        </Stack>
                    ) : multiChoice.displayStyle === 'checkbox' ? (
                        <Group gap="md">
                            {multiChoice.options.map((opt, i) => (
                                <Checkbox key={i} label={opt} readOnly style={{ pointerEvents: 'none' }} />
                            ))}
                        </Group>
                    ) : (
                        <Radio.Group>
                            <Group gap="md">
                                {multiChoice.options.map((opt, i) => (
                                    <Radio key={i} value={opt} label={opt} readOnly style={{ pointerEvents: 'none' }} />
                                ))}
                            </Group>
                        </Radio.Group>
                    )}
                </Box>
            );

        case 'Rating':
            const rating = element as RatingElement;
            return (
                <Box>
                    <Text size="sm" fw={500} mb={4}>{renderLabel(rating.label)}</Text>
                    <Rating count={rating.maxRating} readOnly />
                </Box>
            );

        case 'Button':
            const btn = element as ButtonElement;
            return (
                <Button
                    variant={btn.buttonStyle === 'outline' ? 'outline' : btn.buttonStyle === 'secondary' ? 'light' : 'filled'}
                    fullWidth
                    style={{ pointerEvents: 'none' }}
                >
                    {btn.buttonText}
                </Button>
            );

        case 'RichText':
            const richText = element as RichTextElement;
            const content = isEmptyRichText(richText.content)
                ? '<p style="color: #8E99AB">Enter your rich text here...</p>'
                : richText.content;
            return (
                <Box 
                    p={options.fullPreview ? 0 : 'sm'}
                    style={{ 
                        border: options.fullPreview ? 'none' : '1px solid #E4E7ED',
                        borderRadius: options.fullPreview ? 0 : 8,
                        backgroundColor: options.fullPreview ? 'transparent' : '#F2F4F7',
                        minHeight: 60
                    }}
                >
                    {!options.fullPreview && <Text size="xs" c="dimmed" mb={4}>Rich Text</Text>}
                    <style>{VAR_BUBBLE_CSS}</style>
                    <Box 
                        className="rich-text-render"
                        style={{ 
                            fontSize: '14px',
                            lineHeight: 1.5,
                            overflow: options.fullPreview ? 'visible' : 'hidden',
                            maxHeight: options.fullPreview ? 'none' : 100
                        }}
                        dangerouslySetInnerHTML={{ __html: renderRichTextWithVars(content, previewVars, { showUnsetLabel: true, labels: variableLabels }) }}
                    />
                </Box>
            );

        case 'ImageUpload':
            const imageEl = element as ImageUploadElement;
            return (
                <Box style={{ pointerEvents: 'none' }}>
                    <Text size="sm" fw={500} mb={4}>{renderLabel(imageEl.label)}</Text>
                    <Group
                        gap="xs"
                        align="center"
                        style={{
                            border: '1px dashed #D9DEE8',
                            borderRadius: 8,
                            padding: '12px 14px',
                            backgroundColor: '#FFFFFF',
                        }}
                    >
                        <IconPhoto size={18} style={{ color: '#8A93A3' }} />
                        <Text size="sm" c="dimmed">
                            {imageEl.multiple ? 'Image upload (multiple)' : 'Image upload'}
                            {' · '}
                            {(imageEl.acceptedFormats ?? ['jpg', 'png', 'webp', 'gif']).join(', ')}
                            {' · up to '}
                            {imageEl.maxSizeMB ?? 25}MB
                        </Text>
                    </Group>
                </Box>
            );

        default:
            return <Text c="dimmed">Unknown element type: {element.type}</Text>;
    }
}
